import { addMinutes, isValid, parse } from 'date-fns';

import {
  type Activities_Insert_Input,
  type Activities_Set_Input,
  type Activities_Target_Users_Insert_Input,
  Activity_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import type { LeadActivityAdvancedFormValues } from '@modules/_shared/components/feed/ActivityAdvancedFieldsToggle';
import { toUTCDate } from '@modules/_shared/utils/dates';
import {
  isNullOrEmpty,
  normalizeNullableString,
} from '@modules/_shared/utils/strings';

export const DEFAULT_VISIT_DURATION_MINUTES = 90;
const DEFAULT_CALL_DURATION_MINUTES = 30;
const DEFAULT_TASK_DURATION_MINUTES = 30;

const normalizeOptionalString = (value?: string | null) =>
  isNullOrEmpty(value, true) ? undefined : (value as string).trim();

type ScheduleOptions = {
  date?: string | null;
  time?: string | null;
  durationMinutes: number;
  requireTimeForDueAt?: boolean;
};

type NormalizedSchedule = {
  dueAtIso: string | null;
  endAtIso: string | null;
  startDate?: string;
  startOfDayIso: string | null;
};

const computeSchedule = ({
  date,
  time,
  durationMinutes,
  requireTimeForDueAt = false,
}: ScheduleOptions): NormalizedSchedule => {
  const hasDate = !isNullOrEmpty(date, true);
  const hasTime = !isNullOrEmpty(time, true);

  let normalizedDate = hasDate ? (date as string).trim() : '';
  if (normalizedDate.includes('T')) {
    const dateObj = new Date(normalizedDate);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    normalizedDate = `${year}-${month}-${day}`;
  }

  let startOfDayIso: string | null = null;
  if (hasDate) {
    const parsedDate = parse(normalizedDate, 'yyyy-MM-dd', new Date());
    if (isValid(parsedDate)) {
      startOfDayIso = toUTCDate(parsedDate).toISOString();
    }
  }

  let scheduledAt: Date | null = null;
  if (hasDate && hasTime) {
    const timePart = (time as string).trim();
    const parsedDateTime = parse(
      `${normalizedDate} ${timePart}`,
      'yyyy-MM-dd HH:mm',
      new Date(),
    );
    if (isValid(parsedDateTime)) {
      scheduledAt = parsedDateTime;
    }
  } else if (hasDate && requireTimeForDueAt) {
    const parsedDateTime = parse(
      `${normalizedDate} 00:00`,
      'yyyy-MM-dd HH:mm',
      new Date(),
    );
    if (isValid(parsedDateTime)) {
      scheduledAt = parsedDateTime;
    }
  }

  const dueAtIso = scheduledAt ? scheduledAt.toISOString() : null;
  const endAtIso = scheduledAt
    ? addMinutes(scheduledAt, durationMinutes).toISOString()
    : null;
  const startDate = hasDate ? normalizedDate : undefined;

  if (requireTimeForDueAt && !hasTime) {
    return {
      dueAtIso: null,
      endAtIso: null,
      startDate,
      startOfDayIso,
    };
  }

  return {
    dueAtIso,
    endAtIso,
    startDate,
    startOfDayIso,
  };
};

type BuildScheduledActivityInsertArgs = {
  base: Activities_Insert_Input;
  schedule: NormalizedSchedule;
  activityType: Activity_Types_Enum_Enum;
  done: boolean;
  success: boolean;
  subject?: string | null;
  note?: string | null;
  location?: string | null;
};

const buildScheduledActivityInsert = ({
  base,
  schedule,
  activityType,
  done,
  success,
  subject,
  note,
  location,
}: BuildScheduledActivityInsertArgs): Activities_Insert_Input => {
  const doneAtIso = done
    ? schedule.dueAtIso ?? schedule.startOfDayIso ?? new Date().toISOString()
    : null;

  const result: Activities_Insert_Input = {
    ...base,
    activity_type: activityType,
    subject,
    note,
    ...(location !== undefined ? { location } : {}),
    due_at: schedule.dueAtIso,
    start_date: schedule.startDate,
    end_at: schedule.endAtIso ?? undefined,
  };

  if (done) {
    result.done = done;
    result.success = success;
    result.done_at = doneAtIso;
  }

  return result;
};

type BuildScheduledActivityUpdateArgs = {
  schedule: NormalizedSchedule;
  done: boolean;
  success: boolean;
  subject?: string | null;
  note?: string | null;
  location?: string | null;
  values: LeadActivityAdvancedFormValues;
  isAdmin?: boolean;
};

const setParentRelationship = (
  target: Activities_Set_Input | Activities_Insert_Input,
  parentType: string,
  parentId: string | null,
): void => {
  const normalizedId = normalizeNullableString(parentId);
  if (normalizedId != null) {
    if (parentType === 'Lead') {
      target.lead_id = normalizedId;
    } else if (parentType === 'Lot') {
      target.lot_id = normalizedId;
    } else if (parentType === 'BuyerLead') {
      target.buyer_lead_id = normalizedId;
    } else if (parentType === 'User') {
      target.user_id = normalizedId;
    }
  }
};

const buildScheduledActivityUpdate = ({
  schedule,
  done,
  success,
  subject,
  note,
  location,
  values,
  isAdmin = false,
}: BuildScheduledActivityUpdateArgs): Activities_Set_Input => {
  const doneAt = done ? new Date().toISOString() : null;

  const update: Activities_Set_Input = {
    subject: subject ?? null,
    note: note ?? null,
    due_at: schedule.dueAtIso,
    start_date: schedule.startDate ?? null,
    end_at: schedule.endAtIso ?? null,
    done,
    // Fix: success can only be true when done is true
    success: done ? success : false,
    done_at: doneAt,
  };

  if (location != null) {
    update.location = location;
  }

  update.assigned_to = normalizeOptionalString(values.assigned_to) ?? null;

  if (values.visibility != null) {
    update.visibility = values.visibility;
  }

  if (isAdmin && values.created_by != null) {
    update.created_by = normalizeOptionalString(values.created_by) ?? null;
  }

  if (isAdmin) {
    const parentId = values.parent_id ?? null;
    const parentType = values.parent_type ?? 'Lead';
    setParentRelationship(update, parentType, parentId);
  }

  return update;
};

export const buildBaseActivityInsert = (
  values: LeadActivityAdvancedFormValues,
  currentUserId?: string | null,
  isAdmin?: boolean,
): Activities_Insert_Input => {
  const base: Activities_Insert_Input = {};

  base.assigned_to = normalizeOptionalString(values.assigned_to);

  if (isAdmin) {
    base.created_by =
      normalizeOptionalString(values.created_by) ?? currentUserId ?? null;
  }

  base.visibility = values.visibility ?? 'shared';

  const contactIds = Array.from(
    new Set(
      values.contact_ids
        ?.filter(
          (candidate): candidate is string => !isNullOrEmpty(candidate, true),
        )
        .map(candidate => candidate.trim()),
    ),
  );

  if (contactIds.length > 0) {
    base.activities_target_users = {
      data: contactIds.map(id => ({ target_user_id: id })),
    };
  }

  const parentId = values.parent_id ?? null;
  const parentType = values.parent_type ?? 'Lead';
  setParentRelationship(base, parentType, parentId);

  return base;
};

export const buildTargetUserObjects = (
  activityId: string,
  values: LeadActivityAdvancedFormValues,
): Activities_Target_Users_Insert_Input[] => {
  const ids = new Set<string>();

  if (Array.isArray(values.contact_ids)) {
    values.contact_ids.forEach(candidate => {
      if (!isNullOrEmpty(candidate, true)) {
        ids.add(candidate.trim());
      }
    });
  }

  return Array.from(ids).map(id => ({
    activity_id: activityId,
    target_user_id: id,
  }));
};

type ActivityConfig = {
  type: Activity_Types_Enum_Enum;
  defaultDuration: number;
  requireTimeForDueAt?: boolean;
  computeStatus: (status: string) => { done: boolean; success: boolean };
};

const activityConfigs: Record<string, ActivityConfig> = {
  call: {
    type: Activity_Types_Enum_Enum.Call,
    defaultDuration: DEFAULT_CALL_DURATION_MINUTES,
    computeStatus: status => ({
      done: status !== 'planned',
      success: status === 'successful',
    }),
  },
  visit: {
    type: Activity_Types_Enum_Enum.Visit,
    defaultDuration: DEFAULT_VISIT_DURATION_MINUTES,
    requireTimeForDueAt: true,
    computeStatus: status => ({
      done: status !== 'planned',
      success: status === 'successful',
    }),
  },
  task: {
    type: Activity_Types_Enum_Enum.Task,
    defaultDuration: DEFAULT_TASK_DURATION_MINUTES,
    computeStatus: status => {
      const done = status === 'completed';
      return { done, success: done };
    },
  },
};

type ActivityFormValues = {
  subject?: string;
  notes?: string;
  date?: string | null;
  time?: string;
  duration?: number | null;
  location?: string;
  status: string;
} & LeadActivityAdvancedFormValues;

export const createActivityMappers = (
  activityType: keyof typeof activityConfigs,
) => {
  const config = activityConfigs[activityType];

  const toInsert = (
    values: ActivityFormValues,
    currentUserId?: string | null,
    isAdmin?: boolean,
  ): Activities_Insert_Input => {
    const base = buildBaseActivityInsert(values, currentUserId, isAdmin);
    const durationMinutes =
      values.duration !== undefined && Number.isFinite(values.duration)
        ? Number(values.duration)
        : config.defaultDuration;

    const schedule = computeSchedule({
      date: values.date,
      time: values.time,
      durationMinutes,
      requireTimeForDueAt: config.requireTimeForDueAt,
    });

    const { done, success } = config.computeStatus(values.status);
    const subject = normalizeOptionalString(values.subject);
    const note = normalizeNullableString(values.notes);
    const location = normalizeOptionalString(values.location);

    return buildScheduledActivityInsert({
      base,
      schedule,
      activityType: config.type,
      done,
      success,
      subject,
      note,
      ...(location !== undefined ? { location } : {}),
    });
  };

  const toUpdate = (
    values: ActivityFormValues,
    isAdmin?: boolean,
  ): Activities_Set_Input => {
    const durationMinutes =
      values.duration !== undefined && Number.isFinite(values.duration)
        ? Number(values.duration)
        : config.defaultDuration;

    const schedule = computeSchedule({
      date: values.date,
      time: values.time,
      durationMinutes,
      requireTimeForDueAt: config.requireTimeForDueAt,
    });

    const { done, success } = config.computeStatus(values.status);
    const subject = normalizeOptionalString(values.subject);
    const note = normalizeNullableString(values.notes);
    const location =
      values.location === undefined
        ? undefined
        : normalizeNullableString(values.location);

    return buildScheduledActivityUpdate({
      schedule,
      done,
      success,
      subject,
      note,
      ...(location !== undefined ? { location } : {}),
      values,
      isAdmin,
    });
  };

  return { toInsert, toUpdate };
};

type NoteFormValues = LeadActivityAdvancedFormValues & {
  note: string;
};

export const mapNoteFormValuesToInsert = (
  values: NoteFormValues,
  currentUserId?: string | null,
  isAdmin?: boolean,
): Activities_Insert_Input => {
  const base = buildBaseActivityInsert(values, currentUserId, isAdmin);
  const note = normalizeNullableString(values.note);

  return {
    ...base,
    activity_type: Activity_Types_Enum_Enum.Note,
    note,
    done: true,
    success: true,
    done_at: new Date().toISOString(),
  };
};

export const mapNoteFormValuesToUpdate = (
  values: NoteFormValues,
  isAdmin?: boolean,
): Activities_Set_Input => {
  const note = normalizeNullableString(values.note);

  const update: Activities_Set_Input = {
    note,
    done: true,
    success: true,
    done_at: new Date().toISOString(),
    subject: null,
    due_at: null,
    end_at: null,
    start_date: null,
  };

  update.assigned_to = values.assigned_to ?? null;

  if (isAdmin && values.created_by !== undefined) {
    update.created_by = normalizeNullableString(values.created_by);
  }

  if (values.visibility != null) {
    update.visibility = values.visibility;
  }

  const parentId = values.parent_id ?? null;
  const parentType = values.parent_type ?? 'Lead';

  update.lead_id = null;
  update.lot_id = null;
  update.buyer_lead_id = null;
  update.user_id = null;
  setParentRelationship(update, parentType, parentId);

  return update;
};
